import { formatSimilarityStat } from "./similarSeasonsFormat";

const PER75_TO_PER36 = 36 / 75;

function isMissing(value) {
  return value == null || !Number.isFinite(Number(value));
}

function formatInteger(value) {
  return String(Math.round(Number(value)));
}

function formatOneDecimal(value) {
  return (Math.round((Number(value) + Number.EPSILON) * 10) / 10).toFixed(1);
}

function formatRate3(value) {
  const formatted = Number(value).toFixed(3);
  return formatted.startsWith("0") ? formatted.slice(1) : formatted;
}

function formatPercent(value) {
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function formatPer36From75(value) {
  return formatOneDecimal(Number(value) * PER75_TO_PER36);
}

const FEATURES = {
  age: {
    label: "age",
    gloss: "How old the player was that season",
    format: formatInteger,
  },

  battingAverage: {
    label: "AVG",
    gloss: "Batting average — hits per at-bat",
    format: formatRate3,
  },
  onBasePercentage: {
    label: "OBP",
    gloss: "On-base percentage — how often the batter reaches base",
    format: formatRate3,
  },
  sluggingAverage: {
    label: "SLG",
    gloss: "Slugging — total bases per at-bat",
    format: formatRate3,
  },
  isolatedPower: {
    label: "ISO",
    gloss: "Isolated power — extra-base power beyond singles",
    format: formatRate3,
  },
  hrPerPA: {
    label: "HR/PA",
    gloss: "Home runs per plate appearance",
    format: formatRate3,
  },
  bbPerPA: {
    label: "BB/PA",
    gloss: "Walks per plate appearance",
    format: formatRate3,
  },
  kPerPA: {
    label: "K/PA",
    gloss: "Strikeouts per plate appearance",
    format: formatRate3,
  },
  era: {
    label: "ERA",
    gloss: "Earned runs allowed per nine innings",
    format: formatOneDecimal,
  },
  whip: {
    label: "WHIP",
    gloss: "Walks and hits allowed per inning",
    format: formatOneDecimal,
  },
  kPer9: {
    label: "K/9",
    gloss: "Strikeouts per nine innings",
    format: formatOneDecimal,
  },
  bbPer9: {
    label: "BB/9",
    gloss: "Walks allowed per nine innings",
    format: formatOneDecimal,
  },
  hrPer9: {
    label: "HR/9",
    gloss: "Home runs allowed per nine innings",
    format: formatOneDecimal,
  },
  ipPerGame: {
    label: "IP/G",
    gloss: "Innings pitched per game",
    format: formatOneDecimal,
  },
  saveRate: {
    label: "SV%",
    gloss: "Saves as a share of save opportunities",
    format: formatRate3,
  },

  minutesPerGame: {
    label: "MPG",
    gloss: "Minutes played per game",
    format: formatOneDecimal,
  },
  pointsPer75: {
    label: "PTS/36",
    gloss: "Points scored per 36 minutes",
    format: formatPer36From75,
  },
  assistsPer75: {
    label: "AST/36",
    gloss: "Assists per 36 minutes",
    format: formatPer36From75,
  },
  reboundsPer75: {
    label: "REB/36",
    gloss: "Rebounds per 36 minutes",
    format: formatPer36From75,
  },
  stealsPer75: {
    label: "STL/36",
    gloss: "Steals per 36 minutes",
    format: formatPer36From75,
  },
  blocksPer75: {
    label: "BLK/36",
    gloss: "Blocks per 36 minutes",
    format: formatPer36From75,
  },
  pointsPer36: {
    label: "PTS/36",
    gloss: "Points scored per 36 minutes",
    format: formatOneDecimal,
  },
  assistsPer36: {
    label: "AST/36",
    gloss: "Assists per 36 minutes",
    format: formatOneDecimal,
  },
  reboundsPer36: {
    label: "REB/36",
    gloss: "Rebounds per 36 minutes",
    format: formatOneDecimal,
  },
  stealsPer36: {
    label: "STL/36",
    gloss: "Steals per 36 minutes",
    format: formatOneDecimal,
  },
  blocksPer36: {
    label: "BLK/36",
    gloss: "Blocks per 36 minutes",
    format: formatOneDecimal,
  },
  trueShootingPct: {
    label: "TS%",
    gloss: "True shooting — overall scoring efficiency",
    format: formatPercent,
  },
  turnoverPct: {
    label: "TOV%",
    gloss: "Turnover rate — giveaways per 100 possessions",
    format: formatPercent,
  },
  threePointAttemptRate: {
    label: "3PAr",
    gloss: "Share of shots that are three-pointers",
    format: formatPercent,
  },
  freeThrowRate: {
    label: "FTr",
    gloss: "Free throw attempts relative to field-goal attempts",
    format: formatPercent,
  },

  completionPct: {
    label: "CMP%",
    gloss: "Share of pass attempts that were completed",
    format: formatPercent,
  },
  yardsPerAttempt: {
    label: "Y/A",
    gloss: "Yards gained per attempt",
    format: formatOneDecimal,
  },
  tdPerAtt: {
    label: "TD/Att",
    gloss: "Touchdowns per attempt",
    format: formatRate3,
  },
  intPerAtt: {
    label: "INT/Att",
    gloss: "Interceptions per pass attempt",
    format: formatRate3,
  },
  sackRate: {
    label: "Sk/Att",
    gloss: "Sacks taken per dropback",
    format: formatRate3,
  },
  attPerGame: {
    label: "Att/G",
    gloss: "Attempts per game",
    format: formatOneDecimal,
  },
  rushYards: {
    label: "Yds",
    gloss: "Rushing yards",
    format: formatInteger,
  },
  recYards: {
    label: "Yds",
    gloss: "Receiving yards",
    format: formatInteger,
  },
  recPerGame: {
    label: "Rec/G",
    gloss: "Receptions per game",
    format: formatOneDecimal,
  },
  yardsPerReception: {
    label: "Y/R",
    gloss: "Yards gained per catch",
    format: formatOneDecimal,
  },
  tdPerReception: {
    label: "TD/Rec",
    gloss: "Touchdowns per reception",
    format: formatRate3,
  },
};

export function similarityFeatureDisplay(metric, fallbackLabel) {
  const spec = FEATURES[metric];
  return {
    label: spec?.label || fallbackLabel || metric,
    gloss: spec?.gloss || null,
    format(value) {
      if (isMissing(value)) return "—";
      if (spec?.format) return spec.format(value);
      return formatSimilarityStat(metric, value);
    },
  };
}

export function similarityFeatureGloss(metric) {
  return similarityFeatureDisplay(metric).gloss;
}

export function featureChipAriaLabel(label, gloss) {
  if (!gloss) return undefined;
  return `${label}: ${gloss}`;
}
